import { DOMParser } from '@xmldom/xmldom';
import { logError, logInfo } from '../logger';
import { calculateRouteDistance } from '../routeHelpers';

// Ramer-Douglas-Peucker algorithm implementation
const perpendicularDistance = (point, start, end) => {
  const x = point.latitude;
  const y = point.longitude;
  const x1 = start.latitude;
  const y1 = start.longitude;
  const x2 = end.latitude;
  const y2 = end.longitude;

  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) param = dot / lenSq;

  let xx, yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = x - xx;
  const dy = y - yy;

  return Math.sqrt(dx * dx + dy * dy);
};

const rdpSimplify = (points, epsilon) => {
  if (points.length <= 2) return points;

  let maxDistance = 0;
  let index = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], points[0], points[points.length - 1]);
    if (distance > maxDistance) {
      index = i;
      maxDistance = distance;
    }
  }

  if (maxDistance > epsilon) {
    const firstLine = rdpSimplify(points.slice(0, index + 1), epsilon);
    const secondLine = rdpSimplify(points.slice(index), epsilon);
    return [...firstLine.slice(0, -1), ...secondLine];
  }

  return [points[0], points[points.length - 1]];
};

export const parseGPX = async (content) => {
  try {
    const parser = new DOMParser();
    const gpx = parser.parseFromString(content, 'text/xml');
    const points = [];
    const routes = [];
    const layers = new Set(['gpx']);

    // Parse Waypoints
    const waypoints = gpx.getElementsByTagName('wpt');
    for (let i = 0; i < waypoints.length; i++) {
      const wpt = waypoints[i];
      const latitude = Number(wpt.getAttribute('lat'));
      const longitude = Number(wpt.getAttribute('lon'));
      const name = wpt.getElementsByTagName('name')[0]?.textContent;
      const desc = wpt.getElementsByTagName('desc')[0]?.textContent;
      const sym = wpt.getElementsByTagName('sym')[0]?.textContent;
      
      // Use symbol as layer if available
      const layer = sym || 'waypoints';
      layers.add(layer);

      points.push({
        type: 'Custom',
        coordinates: { latitude, longitude },
        notes: desc || '',
        properties: { name },
        layer
      });
    }

    // Parse Tracks with RDP simplification
    const tracks = gpx.getElementsByTagName('trk');
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const name = track.getElementsByTagName('name')[0]?.textContent;
      let trackPoints = [];

      const tracksegs = track.getElementsByTagName('trkseg');
      for (let j = 0; j < tracksegs.length; j++) {
        const trackseg = tracksegs[j];
        const trkpts = trackseg.getElementsByTagName('trkpt');
        
        const segmentPoints = [];
        for (let k = 0; k < trkpts.length; k++) {
          const trkpt = trkpts[k];
          segmentPoints.push({
            latitude: Number(trkpt.getAttribute('lat')),
            longitude: Number(trkpt.getAttribute('lon')),
            elevation: trkpt.getElementsByTagName('ele')[0]?.textContent || null,
            timestamp: trkpt.getElementsByTagName('time')[0]?.textContent || new Date().toISOString()
          });
        }
        
        // Apply RDP simplification with appropriate epsilon
        const epsilon = 0.00001; // Adjust this value to control simplification level
        const simplifiedSegment = rdpSimplify(segmentPoints, epsilon);
        trackPoints = [...trackPoints, ...simplifiedSegment];
      }

      if (trackPoints.length > 0) {
        const distance = calculateRouteDistance(trackPoints);
        const originalPointCount = trackPoints.length;
        const layer = 'tracks';
        layers.add(layer);
        
        routes.push({
          name: name || `Imported Track ${i + 1}`,
          trackPoints,
          distance,
          color: '#3B82F6', // Default blue color
          properties: {
            source: 'gpx',
            type: 'track',
            originalPointCount,
            layer
          }
        });
      }
    }

    // Parse Routes (different from tracks in GPX)
    const gpxRoutes = gpx.getElementsByTagName('rte');
    for (let i = 0; i < gpxRoutes.length; i++) {
      const route = gpxRoutes[i];
      const name = route.getElementsByTagName('name')[0]?.textContent;
      const rtepts = route.getElementsByTagName('rtept');
      
      const trackPoints = [];
      for (let j = 0; j < rtepts.length; j++) {
        const rtept = rtepts[j];
        trackPoints.push({
          latitude: Number(rtept.getAttribute('lat')),
          longitude: Number(rtept.getAttribute('lon')),
          elevation: rtept.getElementsByTagName('ele')[0]?.textContent || null,
          timestamp: rtept.getElementsByTagName('time')[0]?.textContent || new Date().toISOString()
        });
      }
      
      if (trackPoints.length > 0) {
        const distance = calculateRouteDistance(trackPoints);
        const layer = 'routes';
        layers.add(layer);
        
        routes.push({
          name: name || `Imported Route ${i + 1}`,
          trackPoints,
          distance,
          color: '#EF4444', // Default red color for routes
          properties: {
            source: 'gpx',
            type: 'route',
            layer
          }
        });
      }
    }

    logInfo(`Parsed ${points.length} points and ${routes.length} routes from GPX with ${layers.size} layers`);
    return { points, routes, layers: Array.from(layers) };
  } catch (error) {
    logError('Failed to parse GPX:', error);
    throw new Error('Failed to parse GPX file');
  }
};